import { forwardRef, Fragment, ReactNode } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText, { ListItemTextProps } from '@mui/material/ListItemText';
import MuiMenuItem, { MenuItemProps as MuiMenuItemProps } from '@mui/material/MenuItem';

import useMenu from './useMenu';
import { menuItemClasses } from './utils';

export type MenuItemProps = MuiMenuItemProps &
  Partial<Pick<LinkProps, 'target' | 'to'>> &
  Pick<ListItemTextProps, 'inset'> & {
    icon?: ReactNode;
    keepMounted?: boolean;
    loading?: boolean;
    rightContent?: ReactNode;
    subheading?: string;
  };

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  (
    {
      children,
      disabled,
      divider,
      icon,
      inset,
      keepMounted,
      loading,
      onClick,
      rightContent,
      subheading,
      ...rest
    },
    ref
  ) => {
    const { onClose } = useMenu();

    return (
      <Fragment>
        <MuiMenuItem
          {...rest}
          {...(rest.to ? { component: Link } : {})}
          disabled={loading || disabled}
          onClick={e => {
            onClick?.(e);
            if (!keepMounted) onClose();
          }}
          ref={ref}
        >
          {icon && <ListItemIcon>{icon}</ListItemIcon>}

          <ListItemText
            inset={inset}
            primary={
              <Typography noWrap variant='inherit'>
                {children}
              </Typography>
            }
            secondary={subheading}
          />

          {rightContent && (
            <Box alignItems='center' className={menuItemClasses.rightContent} display='flex'>
              {rightContent}
            </Box>
          )}

          {loading && <CircularProgress size={16} />}
        </MuiMenuItem>

        {divider && <Divider />}
      </Fragment>
    );
  }
);

MenuItem.displayName = 'MenuItem';
